import React from 'react';
import { cn } from '../../lib/utils';

/**
 * StatusBadge — semantic pill for status, types, roles, etc.
 *
 * Props:
 *   tone   'primary'|'success'|'warning'|'danger'|'neutral'|'info'
 *   size   'sm' (default) | 'md'
 *   dot    boolean — show leading dot (default true)
 *   icon   Lucide icon (optional; replaces dot)
 */
const TONES = {
  primary: {
    bg: 'bg-primary-50 dark:bg-primary-900/30',
    text: 'text-primary-700 dark:text-primary-300',
    ring: 'ring-primary-200/60 dark:ring-primary-800/60',
    dot: 'bg-primary-500',
  },
  success: {
    bg: 'bg-success-50 dark:bg-success-900/30',
    text: 'text-success-700 dark:text-success-300',
    ring: 'ring-success-200/60 dark:ring-success-800/60',
    dot: 'bg-success-500',
  },
  warning: {
    bg: 'bg-warning-50 dark:bg-warning-900/30',
    text: 'text-warning-700 dark:text-warning-300',
    ring: 'ring-warning-200/60 dark:ring-warning-800/60',
    dot: 'bg-warning-500',
  },
  danger: {
    bg: 'bg-danger-50 dark:bg-danger-900/30',
    text: 'text-danger-700 dark:text-danger-300',
    ring: 'ring-danger-200/60 dark:ring-danger-800/60',
    dot: 'bg-danger-500',
  },
  info: {
    bg: 'bg-sky-50 dark:bg-sky-900/30',
    text: 'text-sky-700 dark:text-sky-300',
    ring: 'ring-sky-200/60 dark:ring-sky-800/60',
    dot: 'bg-sky-500',
  },
  neutral: {
    bg: 'bg-neutral-100 dark:bg-neutral-800',
    text: 'text-neutral-700 dark:text-neutral-200',
    ring: 'ring-neutral-200/60 dark:ring-neutral-700/60',
    dot: 'bg-neutral-500',
  },
};

const SIZE = {
  sm: 'text-[11px] px-2 py-0.5',
  md: 'text-xs px-2.5 py-1',
};

const StatusBadge = ({
  children,
  tone = 'neutral',
  size = 'sm',
  dot = true,
  icon: Icon = null,
  className = '',
}) => {
  const t = TONES[tone] || TONES.neutral;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full font-medium ring-1 ring-inset whitespace-nowrap',
        t.bg,
        t.text,
        t.ring,
        SIZE[size] || SIZE.sm,
        className,
      )}
    >
      {Icon ? (
        <Icon size={12} aria-hidden="true" />
      ) : dot ? (
        <span className={cn('inline-block h-1.5 w-1.5 rounded-full', t.dot)} aria-hidden="true" />
      ) : null}
      {children}
    </span>
  );
};

export default StatusBadge;
